import { useState, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useToastContext } from '@/contexts/toastContext';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import { getActiveOrgId } from '@/lib/activeOrg';
import financeContractorsService, {
  ManagedContractor,
  TeamOption,
} from '@/services/financeContractors';
import { FetchApiIds } from '@/types';

export type AllocationForm = {
  allocationId?: string;
  profileId: string;
  teamId: string;
  monthlyRate: string;
  allocationPct: string;
  startDate: string;
  endDate: string;
};

const EMPTY_FORM: AllocationForm = {
  profileId: '',
  teamId: '',
  monthlyRate: '',
  allocationPct: '100',
  startDate: new Date().toISOString().split('T')[0],
  endDate: '',
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export default function useContractorManagement() {
  const orgId = getActiveOrgId();
  const queryClient = useQueryClient();
  const { showSuccessFeedback, showErrorFeedback } = useToastContext();

  const { data: managedContractors = [], isLoading: loadingContractors } = useSeoFetch<ManagedContractor[]>(
    ['fx-managed-contractors', orgId],
    () => financeContractorsService.getManagedContractors(),
    { retry: false }
  );

  const { data: teams = [] } = useSeoFetch<TeamOption[]>(
    ['fx-teams', orgId],
    () => financeContractorsService.getTeams(),
    { retry: false }
  );

  const [editDialog, setEditDialog] = useState<AllocationForm | null>(null);
  const [saving, setSaving] = useState(false);

  const openAdd = useCallback((profileId?: string) => {
    setEditDialog({ ...EMPTY_FORM, profileId: profileId ?? '' });
  }, []);

  const openEdit = useCallback((c: ManagedContractor) => {
    setEditDialog({
      allocationId: c.allocationId,
      profileId: c.profileId,
      teamId: c.teamId,
      monthlyRate: String(c.monthlyRate),
      allocationPct: String(c.allocationPct),
      startDate: c.startDate ?? '',
      endDate: c.endDate ?? '',
    });
  }, []);

  const closeDialog = useCallback(() => {
    setEditDialog(null);
  }, []);

  const handleSave = useCallback(async (form: AllocationForm) => {
    setSaving(true);
    try {
      await financeContractorsService.upsertAllocation({
        allocationId: form.allocationId,
        profileId: form.profileId,
        teamId: form.teamId,
        monthlyRate: parseFloat(form.monthlyRate) || 0,
        allocationPct: parseFloat(form.allocationPct) || 100,
        startDate: form.startDate,
        endDate: form.endDate || null,
      });
      queryClient.invalidateQueries('fx-managed-contractors');
      queryClient.invalidateQueries(FetchApiIds.FX_DASHBOARD_SUMMARY);
      setEditDialog(null);
      showSuccessFeedback('Contractor saved successfully');
    } catch (err: any) {
      showErrorFeedback(err?.message || 'Failed to save contractor');
    } finally {
      setSaving(false);
    }
  }, [queryClient, showSuccessFeedback, showErrorFeedback]);

  const handleRemove = useCallback(async (allocationId: string) => {
    try {
      await financeContractorsService.removeAllocation(allocationId);
      queryClient.invalidateQueries('fx-managed-contractors');
      queryClient.invalidateQueries(FetchApiIds.FX_DASHBOARD_SUMMARY);
      showSuccessFeedback('Contractor removed');
    } catch (err: any) {
      showErrorFeedback(err?.message || 'Failed to remove contractor');
    }
  }, [queryClient, showSuccessFeedback, showErrorFeedback]);

  return {
    managedContractors,
    loadingContractors,
    teams,
    editDialog,
    saving,
    openAdd,
    openEdit,
    closeDialog,
    handleSave,
    handleRemove,
  };
}
